import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";

import { css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { Kernel } from "../Kernel";
import { BaseElement } from "./BaseElement";
import { Style } from "./Styles";
import { makeDefaultFilter } from "../Filter";
import { IPlaceFilter } from "../backend/cloud/PlaceFilterTable";
import { litIf } from "./LitUtils";
import { FluentIterable } from "fluent-iterable";

type DataTableSelectDataSource = {
    table_changed_event: string;
    selected_changed_event: string;
    all: () => Promise<FluentIterable<any>>;
    getCurrentItem: () => string | undefined;
    read: (id: string) => any;
    create: (name: string, isDefault: boolean) => any;
};

class UserFiltersDataSource implements DataTableSelectDataSource {
    public table_changed_event = "filters-table-changed";
    public selected_changed_event = "filter-changed";

    constructor(private readonly _kernel: Kernel) {}

    private _filterTable = Kernel.instance().dataStore.getSource("place-filters");

    async all() {
        return this._filterTable.all();
    }

    getCurrentItem() {
        return Kernel.instance().currentPlaceFilter.get().id;
    }
    async create(name: string) {
        return await this._filterTable.create({
            name,
            filter: makeDefaultFilter(),
            table: "user-filters",
        } as IPlaceFilter);
    }
    async read(id: string) {
        return this._filterTable.read(id);
    }
}

@customElement("ds-data-table-select")
export class DataTableSelect extends BaseElement {
    static override styles = [
        Style,
        css`
            .select-item {
                grid-row: 1;
                grid-column: 1;
            }

            .input-new-item {
                grid-row: 2;
                grid-column: 1;
            }

            .add-new-item {
                display: grid;
                grid-template-columns: auto;
                grid-template-rows: auto;
                grid-column-gap: 0.5rem;
                grid-row-gap: 0.5rem;
                overflow-x: clip;
            }

            .select-row {
                align-items: center;
            }

            .select-row sl-select {
                flex-grow: 1;
            }

            .select-row sl-icon {
                font-size: 1.8em;
            }

            sl-select {
                padding: 0.2em;
            }
        `,
    ];

    private readonly TableInterfaces: { [key: string]: DataTableSelectDataSource } = {
        userFilters: new UserFiltersDataSource(this._kernel),
    };

    @property({ type: String }) table?: string;
    @property({ type: Boolean }) showAddNew = false;
    @property({ type: Boolean }) showAddButton = false;

    @state() protected _items: Array<any> = [];
    @state() protected _selectedItem?: string;
    @state() protected _updateCount = 0;

    constructor() {
        super();
    }

    private _onTableChanged(items: any[]) {
        this._update(items);
    }

    private _onSelectedChanged(item: any) {
        this._setCurrentItem(item.id);
    }

    private _setCurrentItem(item: string) {
        this._selectedItem = item;
    }

    private _update(items: any[]) {
        if (!this.table) {
            return;
        }

        this._items = items;
        const currentItem = this.TableInterfaces[this.table].getCurrentItem();
        if (currentItem) {
            this._setCurrentItem(currentItem);
        }

        this.requestUpdate();
        ++this._updateCount;
    }

    private async _addNew() {
        if (!this.table) {
            return;
        }
        const input = this.shadowRoot?.getElementById("new-name-input") as HTMLInputElement;
        let name = input?.value;
        if (name.length === 0) {
            name = new Date().toLocaleString();
        }
        if (name && name.length) {
            const newItem = await this.TableInterfaces[this.table].create(name, false);
            this._selectItem(newItem.id);
            input.value = "";
        }
    }

    private async _selectItem(itemId: string) {
        if (this.table) {
            const table = this.TableInterfaces[this.table];
            this._setCurrentItem(itemId);
            const item = await table.read(itemId);
            if (item) {
                this._kernel.events.raiseEvent(table.selected_changed_event, item);
            }
            ++this._updateCount;
        }
    }

    private async _selectClickEvent(event: any) {
        if (!this.table) {
            return;
        }
        const select = event.target as HTMLSelectElement;
        if (select) {
            if (select.value === "add-new") {
                this.TableInterfaces[this.table].create(Date.now().toLocaleString(), false);
            } else {
                let id = select.value;
                id = id.substring(0, id.indexOf("&"));
                if (this._selectedItem !== id) {
                    await this._selectItem(id);
                }
            }
        }
    }

    override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        if (this.table) {
            const table = this.TableInterfaces[this.table];

            // All listeners are removed automatically in BaseElement.
            this._subscribeToKernelEvent(table.table_changed_event, (items: Array<any>) =>
                this._onTableChanged(items)
            );
            this._subscribeToKernelEvent(table.selected_changed_event, (item: any) =>
                this._onSelectedChanged(item)
            );

            this._items = [...(await table.all())];
            const curItem = table.getCurrentItem();
            if (curItem) {
                this._selectItem(curItem);
            } else {
                this._selectedItem = undefined;
            }
            this._selectedItem = table.getCurrentItem();
        }
    }

    override render() {
        return html`${!this.table
            ? html``
            : html`<div class="add-new-item">
                  <div class="select-row flex-row">
                      <sl-select
                          pill
                          filled
                          size="small"
                          hoist
                          class="select-item"
                          @sl-change=${this._selectClickEvent}
                          value="${this._selectedItem ?? ""}&${this._updateCount}"
                          id="selected-id"
                      >
                          ${this._items.map(
                              (n) =>
                                  html`<sl-option value="${n.id}&${this._updateCount}"
                                      >${n.name}</sl-option
                                  >`
                          )}
                      </sl-select>
                      ${litIf(
                          this.showAddButton,
                          () => html`
                              <sl-icon
                                  name="plus-circle"
                                  slot="suffix"
                                  @click=${() => this._raiseEvent("ds-add-new")}
                              ></sl-icon>
                          `
                      )}
                  </div>
                  ${litIf(
                      this.showAddNew,
                      () => html`
                          <sl-input
                              autocomplete="off"
                              class="input-new-item"
                              id="new-name-input"
                              placeholder="Enter new filter name"
                              @sl-change=${this._addNew}
                          >
                              <sl-icon
                                  name="plus-circle"
                                  slot="suffix"
                                  @click=${this._addNew}
                              ></sl-icon>
                          </sl-input>
                      `
                  )}
              </div>`}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-data-table-select": DataTableSelect;
    }
}
